import React from "react";
import { AbsoluteFill, Easing, interpolate } from "remotion";
import { PASS_DECOR, passCodes, passLayout, tripLegs } from "./boardingData";
import { Glyph, IconTile, PAPER, TEAL, blurredMap, tealShadow } from "./houseStyle";
import {
	BP_ARC,
	BP_BOARDED,
	BP_CLEAR,
	BP_ENTER,
	BP_FLY,
	BP_LIFT,
	BP_PASS_OUT,
	BP_SCAN,
	BP_SHEEN,
	BP_TEAR,
	IntroLayout,
	TS_DROPS,
	TS_EVERY,
	TS_LEAVE,
	TS_PUNCH,
	TS_SPREAD,
	TS_TICKETS,
	overlayBox,
} from "./introCamera";
import { scatteredRandom } from "./introDrawing";
import { passportStamps, pickEvenly, shakeAt, slamAt, stampHits } from "./passportStamps";

/**
 * The boarding pass and the ticket stack, drawn over the map while the camera holds the whole trip
 * behind them (`HOLDS_THE_TRIP` in introCamera.ts). What they show is boardingData.ts; this only draws.
 * The reel's own header is laid over both.
 */

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;
const ease = (frame: number, window: number[], easing: (t: number) => number = Easing.inOut(Easing.cubic)) =>
	interpolate(frame, window, [0, 1], { ...clamp, easing });
const MUTED = "#6b7c7a";

type BoardingProps = {
	frame: number;
	width: number;
	height: number;
	px: (n: number) => number;
	layout: IntroLayout;
	reelData: any[];
	currentTrip: any;
	/** The map's legs, for their kilometres. */
	paths: any[];
	config: { from?: string; to?: string; fromCity?: string };
	nightsText: string;
	stopsText: string;
	brandColor: string;
	/** The first stop's pixel on this frame: where the plane lands. */
	stopXY: number[];
	/** The reel's header, which the intro fades itself. */
	words: React.ReactNode;
};

/** A boarding pass's code: 21 modules, the three finder squares, the rest from `seed`. Decoration, not data. */
const PassCode = ({ size, seed }: { size: number; seed: number }) => {
	const n = 21;
	const random = scatteredRandom(seed);
	// Finder squares in three corners, as on every QR code; not the fourth.
	const corner = (x: number, y: number) => x < 7 && y < 7;
	const inFinder = (x: number, y: number) => corner(x, y) || corner(n - 1 - x, y) || corner(x, n - 1 - y);
	const cells: React.ReactNode[] = [];
	for (let y = 0; y < n; y++)
		for (let x = 0; x < n; x++)
			if (!inFinder(x, y) && random() > 0.52) cells.push(<rect key={`${x}-${y}`} x={x} y={y} width={1.02} height={1.02} fill={TEAL} />);
	const eye = (x: number, y: number) => (
		<g key={`eye-${x}-${y}`} transform={`translate(${x} ${y})`}>
			<rect x={0.5} y={0.5} width={6} height={6} fill="none" stroke={TEAL} strokeWidth={1} />
			<rect x={2} y={2} width={3} height={3} fill={TEAL} />
		</g>
	);
	return (
		<svg width={size} height={size} viewBox={`0 0 ${n} ${n}`} style={{ display: "block" }}>
			{cells}
			{eye(0, 0)}
			{eye(n - 7, 0)}
			{eye(0, n - 7)}
		</svg>
	);
};

// ---- boarding-pass ------------------------------------------------------------------------------------

const BoardingPass = (p: BoardingProps) => {
	const { frame, px, height, reelData, currentTrip, config, brandColor, stopXY, words } = p;
	const codes = passCodes(config, reelData);
	const first = passportStamps(reelData, currentTrip)[0];
	const room = overlayBox(p.layout, p.width, height);
	// Drawn on a 980-wide pass and scaled to the room: `q` is a unit of that pass.
	const W = Math.min(room.width * 0.94, px(980));
	const q = (n: number) => (n * W) / 980;
	const H = q(560);
	const left = room.left + (room.width - W) / 2;
	const top = room.top + (room.height - H) / 2;
	const cut = q(748);
	const nights = String(p.nightsText).replace(/\D/g, "");
	const stops = String(p.stopsText).replace(/\D/g, "");

	const enter = 1 - ease(frame, BP_ENTER, Easing.out(Easing.cubic));
	const arc = ease(frame, BP_ARC);
	const sheen = interpolate(frame, BP_SHEEN, [-0.4, 1.4], clamp);
	const scan = interpolate(frame, BP_SCAN, [0, 1], clamp);
	const boarded = slamAt(frame, BP_BOARDED);
	const flash = interpolate(frame, [BP_BOARDED - 1, BP_BOARDED, BP_BOARDED + 8], [0, 1, 0], clamp);
	const tear = ease(frame, BP_TEAR, Easing.in(Easing.quad));
	const passOut = ease(frame, BP_PASS_OUT, Easing.in(Easing.cubic));
	const [dx, dy] = shakeAt(frame, [BP_BOARDED], px(12));

	// The plane crossing from code to code, along a dotted arc in pass units. Its ends are set by how
	// wide the two codes set: fixed at 300 and 560, the plane sat on AMS's S and then parked on CAI's C.
	const fit = passLayout(codes.from, codes.to);
	const a0 = [q(fit.arcFrom), q(196)];
	const a1 = [q(fit.arcTo), q(196)];
	const bend = [q((fit.arcFrom + fit.arcTo) / 2), q(118)];
	const onArc = (t: number) => {
		const u = 1 - t;
		return [u * u * a0[0] + 2 * u * t * bend[0] + t * t * a1[0], u * u * a0[1] + 2 * u * t * bend[1] + t * t * a1[1]];
	};
	const arcPath = Array.from({ length: 33 }, (_, i) => onArc((i / 32) * arc))
		.map((pt, i) => `${i ? "L" : "M"}${pt[0].toFixed(1)} ${pt[1].toFixed(1)}`)
		.join(" ");
	const onPass = onArc(arc);

	// The take-off: the plane leaves the pass as the route's icon tile and flies a curve to the first stop.
	const lift = ease(frame, BP_LIFT, Easing.out(Easing.back(1.6)));
	const fly = ease(frame, BP_FLY, Easing.inOut(Easing.quad));
	const from = [left + dx + onPass[0], top + dy + onPass[1]];
	const via = [(from[0] + stopXY[0]) / 2 + px(160), Math.min(from[1], stopXY[1]) - px(260)];
	const flightAt = (t: number) => {
		const u = 1 - t;
		return [u * u * from[0] + 2 * u * t * via[0] + t * t * stopXY[0], u * u * from[1] + 2 * u * t * via[1] + t * t * stopXY[1]];
	};
	const plane = flightAt(fly);
	const land = ease(frame, [BP_FLY[1] - 4, BP_FLY[1] + 2]);
	const tile = px(40) + px(56) * lift - px(40) * land;
	const trail = Array.from({ length: 18 }, (_, i) => flightAt((fly * i) / 17));
	const ring = interpolate(frame, [BP_FLY[1] - 2, BP_FLY[1] + 10], [0, 1], clamp);

	const label = { fontFamily: "Poppins", fontWeight: 600, fontSize: q(15), letterSpacing: "0.2em", color: MUTED } as const;
	const value = { fontFamily: "Poppins", fontWeight: 700, fontSize: q(32), color: TEAL, lineHeight: 1.1 } as const;
	const cell = (l: string, v: string) => (
		<div>
			<div style={label}>{l}</div>
			<div style={value}>{v}</div>
		</div>
	);
	const code = {
		position: "absolute" as const,
		top: q(122),
		fontFamily: "Poppins",
		fontWeight: 800,
		fontSize: q(fit.size),
		letterSpacing: "-0.02em",
		color: TEAL,
		lineHeight: 1,
	};
	const band = { position: "absolute" as const, left: 0, right: 0, top: 0, height: q(96), background: `linear-gradient(100deg, ${TEAL}, #16606f)` };

	return (
		<AbsoluteFill>
			<AbsoluteFill style={{ ...blurredMap(px, 0.6), opacity: 1 - ease(frame, BP_CLEAR) }} />
			<div
				style={{
					position: "absolute",
					left: left + dx,
					top: top + dy + enter * height * 0.6 + passOut * height * 0.7,
					width: W,
					height: H,
					transformOrigin: "50% 100%",
					transform: `perspective(${px(2400)}px) rotateX(${10 + 18 * enter + 40 * passOut}deg) rotate(${-2 - 4 * enter}deg)`,
					filter: `drop-shadow(0 ${px(30)}px ${px(40)}px rgba(0,0,0,.45))`,
				}}
			>
				<div
					style={{
						position: "absolute",
						left: 0,
						top: 0,
						width: cut,
						height: H,
						background: PAPER,
						borderRadius: `${q(22)}px 0 0 ${q(22)}px`,
						overflow: "hidden",
					}}
				>
					<div style={band}>
						<div
							style={{
								position: "absolute",
								left: q(40),
								top: q(26),
								display: "flex",
								alignItems: "center",
								gap: q(12),
								fontFamily: "Poppins",
								fontWeight: 800,
								fontSize: q(34),
								letterSpacing: "0.06em",
								color: "#fff",
							}}
						>
							<svg width={q(40)} height={q(40)} viewBox={`${-q(20)} ${-q(20)} ${q(40)} ${q(40)}`}>
								<Glyph name="plane" size={q(40)} color="#fff" />
							</svg>
							STIPPL AIR
						</div>
						<div
							style={{
								position: "absolute",
								right: q(36),
								top: q(40),
								fontFamily: "Poppins",
								fontWeight: 600,
								fontSize: q(16),
								letterSpacing: "0.3em",
								color: "#9fe6cf",
							}}
						>
							BOARDING PASS
						</div>
					</div>
					<div style={{ ...code, left: q(40) }}>{codes.from}</div>
					<div style={{ ...code, right: q(40) }}>{codes.to}</div>
					<div style={{ position: "absolute", left: q(44), top: q(240), ...label, letterSpacing: "0.08em", fontSize: q(20) }}>{codes.fromCity}</div>
					<div style={{ position: "absolute", right: q(44), top: q(240), ...label, letterSpacing: "0.08em", fontSize: q(20) }}>{codes.toCity}</div>
					<svg width={cut} height={H} style={{ position: "absolute", inset: 0 }}>
						<path d={arcPath} fill="none" stroke={TEAL} strokeOpacity={0.5} strokeWidth={q(4)} strokeLinecap="round" strokeDasharray={`${q(1)} ${q(12)}`} />
						{frame < BP_LIFT[0] ? (
							<g transform={`translate(${onPass[0]} ${onPass[1]})`}>
								<Glyph name="plane" size={q(46)} color={brandColor} />
							</g>
						) : null}
					</svg>
					<div
						style={{ position: "absolute", left: q(44), right: q(40), top: q(300), display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr 0.8fr", rowGap: q(22) }}
					>
						{cell("PASSENGER", PASS_DECOR.passenger)}
						{cell("DATE", first?.when || "")}
						{cell("BOARDING", PASS_DECOR.boarding)}
						{cell("GATE", PASS_DECOR.gate)}
						{cell("SEAT", PASS_DECOR.seat)}
						{cell("NIGHTS", nights)}
					</div>
					{/* The hologram: a foil oval whose colours shift as the pass moves. */}
					<div
						style={{
							position: "absolute",
							right: q(34),
							bottom: q(34),
							width: q(120),
							height: q(78),
							borderRadius: "50%",
							background: "linear-gradient(115deg, #ff9ecf, #ffe29a, #9ff5d8, #9ec9ff, #d9a8ff, #ff9ecf)",
							backgroundSize: "300% 300%",
							backgroundPosition: `${(frame * 4) % 300}% 50%`,
							boxShadow: "inset 0 0 12px rgba(255,255,255,.8)",
							opacity: 0.85,
						}}
					/>
					{/* BOARDED, slammed on once the code is scanned. HTML text, so a CSS scale is safe (see StampAt). */}
					{boarded ? (
						<div
							style={{
								position: "absolute",
								// Where STOPS would be: the stops are in the header's pills and on the stub.
								left: q(420),
								top: q(392),
								padding: `${q(8)}px ${q(22)}px`,
								border: `${q(6)}px solid #0b8f68`,
								borderRadius: q(14),
								fontFamily: "Poppins",
								fontWeight: 800,
								fontSize: q(44),
								letterSpacing: "0.08em",
								color: "#0b8f68",
								mixBlendMode: "multiply",
								opacity: 0.9 * boarded.opacity,
								transform: `rotate(-9deg) scale(${boarded.scale})`,
							}}
						>
							BOARDED
						</div>
					) : null}
					{/* A gloss that crosses the pass once as it settles. */}
					<div
						style={{
							position: "absolute",
							inset: 0,
							background: `linear-gradient(105deg, transparent ${sheen * 100 - 12}%, rgba(255,255,255,.55) ${sheen * 100}%, transparent ${sheen * 100 + 12}%)`,
							mixBlendMode: "screen",
						}}
					/>
				</div>
				<div style={{ position: "absolute", left: cut - q(1), top: q(10), bottom: q(10), borderLeft: `${q(3)}px dashed #c9cfcc`, opacity: 1 - tear }} />
				{/* The stub, torn off after the scan. */}
				<div
					style={{
						position: "absolute",
						left: cut,
						top: 0,
						width: W - cut,
						height: H,
						background: PAPER,
						borderRadius: `0 ${q(22)}px ${q(22)}px 0`,
						overflow: "hidden",
						transformOrigin: "0 0",
						transform: `translate(${tear * q(60)}px, ${tear * H * 1.3}px) rotate(${tear * 22}deg)`,
						opacity: 1 - ease(frame, [BP_TEAR[1] - 4, BP_TEAR[1]]),
					}}
				>
					<div style={band}>
						<div
							style={{
								position: "absolute",
								left: q(24),
								top: q(38),
								fontFamily: "Poppins",
								fontWeight: 700,
								fontSize: q(18),
								letterSpacing: "0.3em",
								color: "#fff",
							}}
						>
							STIPPL
						</div>
					</div>
					<div style={{ position: "absolute", left: q(24), top: q(112), ...label }}>
						{codes.from} <span style={{ color: TEAL }}>→</span> {codes.to}
					</div>
					<div style={{ position: "absolute", left: (W - cut - q(160)) / 2, top: q(160) }}>
						<PassCode size={q(160)} seed={String(currentTrip?.name || "stippl").length * 97 + 7} />
						{scan > 0 && scan < 1 ? (
							<div
								style={{
									position: "absolute",
									left: -q(10),
									right: -q(10),
									top: scan * q(160),
									height: q(4),
									background: "#ff3b4d",
									boxShadow: `0 0 ${q(14)}px ${q(4)}px rgba(255,59,77,.7)`,
								}}
							/>
						) : null}
					</div>
					<div style={{ position: "absolute", left: q(24), right: q(24), top: q(348), ...label, fontSize: q(14), textAlign: "center" }}>
						{nights} NIGHTS · {stops} STOPS
					</div>
					<AbsoluteFill style={{ background: "#13C892", opacity: flash * 0.45 }} />
				</div>
			</div>
			{frame >= BP_LIFT[0] && frame < BP_FLY[1] + 12 ? (
				<>
					<svg width={p.width} height={height} style={{ position: "absolute", inset: 0 }}>
						<path
							d={trail.map((pt, i) => `${i ? "L" : "M"}${pt[0].toFixed(1)} ${pt[1].toFixed(1)}`).join(" ")}
							fill="none"
							stroke="#fff"
							strokeWidth={px(6)}
							strokeLinecap="round"
							strokeDasharray={`${px(2)} ${px(18)}`}
							opacity={1 - land}
						/>
						{ring > 0 && ring < 1 ? (
							<circle
								cx={stopXY[0]}
								cy={stopXY[1]}
								r={px(20) + ring * px(110)}
								fill="none"
								stroke={brandColor}
								strokeWidth={px(8) * (1 - ring)}
								opacity={1 - ring}
							/>
						) : null}
						{frame < BP_FLY[1] + 2 ? (
							<g transform={`translate(${plane[0]} ${plane[1]})`} style={{ filter: `drop-shadow(0 ${px(10) * lift}px ${px(14)}px rgba(0,0,0,.35))` }}>
								<IconTile size={tile} glyph="plane" color={brandColor} facing={stopXY[0] < from[0] ? -1 : 1} />
							</g>
						) : null}
					</svg>
				</>
			) : null}
			{words}
		</AbsoluteFill>
	);
};

// ---- ticket-stack ------------------------------------------------------------------------------------

/** A ticket's colour and heading by how the leg travels. */
const TICKET: Record<string, { band: string; label: string }> = {
	plane: { band: TEAL, label: "BOARDING PASS" },
	train: { band: "#ff6a2b", label: "TRAIN TICKET" },
	boat: { band: "#3355d6", label: "FERRY TICKET" },
	bus: { band: "#7a3d9a", label: "COACH TICKET" },
	car: { band: "#e84d7e", label: "ROAD TRIP" },
	hike: { band: "#0b8f68", label: "ON FOOT" },
};
/** Laid out, a ticket is at most this size of itself on the pile, and at least this much of it shows under the next: its route. */
const LAID_OUT = 0.8;
const SHOWS = 0.6;

const TicketStack = (p: BoardingProps) => {
	const { frame, px, height, reelData, currentTrip, paths, words } = p;
	const tickets = pickEvenly(tripLegs(passportStamps(reelData, currentTrip), paths), TS_TICKETS);
	const n = tickets.length;
	const hits = stampHits(n, TS_DROPS, TS_EVERY);
	const room = overlayBox(p.layout, p.width, height);
	const W = Math.min(room.width * 0.86, px(900));
	const H = W * 0.34;
	const stubW = W * 0.22;
	const cx = room.left + room.width / 2;
	const cy = room.top + room.height / 2;
	const spread = ease(frame, TS_SPREAD, Easing.out(Easing.back(1.1)));
	// Seven tickets in the room still each show their route: the more there are, the smaller they lie.
	const laidOut = Math.min(LAID_OUT, room.height / (H * (SHOWS * Math.max(0, n - 1) + 1)));
	const out = ease(frame, TS_LEAVE, Easing.in(Easing.cubic));
	const [dx, dy] = shakeAt(frame, hits, px(12));
	const step = n > 1 ? Math.min(H * laidOut * 0.64, (room.height - H * laidOut) / (n - 1)) : 0;
	const listX = (j: number) => (j % 2 ? 1 : -1) * px(14);
	const listY = (j: number) => room.top + (H * laidOut) / 2 + j * step - cy;
	// The first leg's ticket is punched once they are laid out: a hole in its stub, and the dot flying off.
	const punched = frame >= TS_PUNCH;
	const dot = ease(frame, [TS_PUNCH, TS_PUNCH + 10], Easing.out(Easing.quad));
	const hole = `radial-gradient(circle at ${stubW / 2}px ${H * 0.24}px, transparent ${px(18)}px, #000 ${px(19)}px)`;

	return (
		<AbsoluteFill>
			<AbsoluteFill style={{ ...blurredMap(px, 0.6), opacity: 1 - out }} />
			{tickets.map((t, j) => {
				const slam = slamAt(frame, hits[j]);
				if (!slam) return null;
				const random = scatteredRandom(j * 31 + 5);
				const pile = { x: (random() - 0.5) * px(50), y: -j * px(9), rot: (random() - 0.5) * 14 };
				const x = pile.x + (listX(j) - pile.x) * spread;
				const y = pile.y + (listY(j) - pile.y) * spread;
				const rot = pile.rot + ((j % 2 ? 1.5 : -1.5) - pile.rot) * spread;
				const look = TICKET[t.glyph] ?? TICKET.car;
				return (
					<div
						key={j}
						style={{
							position: "absolute",
							left: cx - W / 2 + x + dx,
							top: cy - H / 2 + y + dy + out * height * 0.8,
							width: W,
							height: H,
							transform: `rotate(${rot}deg) scale(${slam.scale * (1 - (1 - laidOut) * spread)})`,
							opacity: slam.opacity,
							filter: `drop-shadow(0 ${px(12)}px ${px(18)}px rgba(0,0,0,.35))`,
							WebkitMaskImage: punched && j === 0 ? hole : undefined,
							maskImage: punched && j === 0 ? hole : undefined,
						}}
					>
						<div style={{ position: "absolute", inset: 0, borderRadius: px(18), background: PAPER, overflow: "hidden" }}>
							<div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: stubW, background: look.band, display: "grid", placeItems: "center" }}>
								<svg width={stubW * 0.62} height={stubW * 0.62} viewBox="-50 -50 100 100">
									<Glyph name={t.glyph} size={100} color="#fff" />
								</svg>
							</div>
							<div style={{ position: "absolute", left: stubW - px(2), top: px(8), bottom: px(8), borderLeft: `${px(3)}px dashed rgba(255,255,255,.7)` }} />
							<div
								style={{
									position: "absolute",
									left: stubW + px(28),
									right: px(24),
									top: px(18),
									display: "flex",
									justifyContent: "space-between",
									fontFamily: "Poppins",
									fontWeight: 700,
									fontSize: px(18),
									letterSpacing: "0.22em",
									color: look.band,
								}}
							>
								<span>{look.label}</span>
								<span style={{ color: MUTED }}>{t.when}</span>
							</div>
							<div
								style={{
									position: "absolute",
									left: stubW + px(28),
									right: px(24),
									top: H * 0.3,
									fontFamily: "Poppins",
									fontWeight: 800,
									// The longest route still on one line: about 0.62 of the size per character.
									fontSize: Math.min(px(52), (W - stubW - px(52)) / (0.62 * Math.max(10, t.from.length + t.to.length + 3))),
									color: TEAL,
									whiteSpace: "nowrap",
									lineHeight: 1.1,
								}}
							>
								{t.from} <span style={{ color: look.band }}>→</span> {t.to}
							</div>
							<div
								style={{
									position: "absolute",
									left: stubW + px(28),
									right: px(24),
									bottom: px(16),
									display: "flex",
									justifyContent: "space-between",
									fontFamily: "Poppins",
									fontWeight: 600,
									fontSize: px(18),
									letterSpacing: "0.2em",
									color: MUTED,
								}}
							>
								<span>{t.km ? `${t.km.toLocaleString("en")} KM` : ""}</span>
								<span>ADMIT ONE</span>
							</div>
						</div>
					</div>
				);
			})}
			{punched && dot < 1 && n ? (
				<div
					style={{
						position: "absolute",
						left: cx - (W * laidOut) / 2 + listX(0) + stubW * laidOut * 0.5 - px(18) + dot * px(140),
						top: room.top + H * laidOut * 0.24 - px(18) - dot * px(120) + dot * dot * px(300) + out * height * 0.8,
						width: px(36),
						height: px(36),
						borderRadius: "50%",
						background: (TICKET[tickets[0].glyph] ?? TICKET.car).band,
						boxShadow: tealShadow(px(4), px(8), 0.3),
						opacity: 1 - dot,
					}}
				/>
			) : null}
			{words}
		</AbsoluteFill>
	);
};

export const BoardingIntro = ({ style, ...p }: BoardingProps & { style: "boarding-pass" | "ticket-stack" }) =>
	style === "boarding-pass" ? <BoardingPass {...p} /> : <TicketStack {...p} />;
